import { useCallback, useEffect, useState } from "react";
import { ArrowDown, ArrowUp, ChevronLeft, ChevronRight, Download, Search } from "lucide-react";
import { toast } from "sonner";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { errMsg, useApi } from "@/lib/api";

export function DataTable({ endpoint, params = {}, columns, rowKey, csvName, selectable, selected = [], onSelect, testid, reloadKey }) {
  const api = useApi();
  const [q, setQ] = useState("");
  const [page, setPage] = useState(1);
  const [sort, setSort] = useState(columns.find((c) => c.defaultSort)?.key || "created_at");
  const [dir, setDir] = useState("desc");
  const [data, setData] = useState(null);
  const [denied, setDenied] = useState("");
  const pkey = JSON.stringify(params);

  const load = useCallback(() => {
    api.get(endpoint, { params: { ...JSON.parse(pkey), q, page, sort, dir, size: 20 } })
      .then((r) => { setData(r.data); setDenied(""); }).catch((e) => { if (e?.response?.status === 403) setDenied("You don't have access to this page."); else toast.error(errMsg(e)); });
  }, [api, endpoint, pkey, q, page, sort, dir]);
  useEffect(() => { const t = setTimeout(load, 250); return () => clearTimeout(t); }, [load, reloadKey]);
  useEffect(() => { setPage(1); }, [q, pkey]);

  const exportCsv = async () => {
    try {
      const r = await api.get(endpoint, { params: { ...params, q, format: "csv" }, responseType: "blob" });
      const url = URL.createObjectURL(r.data);
      Object.assign(document.createElement("a"), { href: url, download: `${csvName}.csv` }).click();
      URL.revokeObjectURL(url);
    } catch (e) { toast.error(errMsg(e)); }
  };
  const toggleSort = (k) => { if (sort === k) setDir(dir === "asc" ? "desc" : "asc"); else { setSort(k); setDir("asc"); } };
  const items = data?.items || [];
  const pages = data ? Math.max(1, Math.ceil(data.total / data.size)) : 1;
  const allSel = items.length > 0 && items.every((i) => selected.includes(i[rowKey]));
  if (denied) return <p className="rounded-xl border border-slate-200 bg-white p-6 text-slate-700" data-testid={`${testid}-denied`}>{denied}</p>;

  return (
    <div data-testid={testid}>
      <div className="mb-3 flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between">
        <div className="relative sm:w-72">
          <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-500" aria-hidden="true" />
          <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search" aria-label="Search table" className="pl-9" data-testid={`${testid}-search`} />
        </div>
        {csvName && <Button variant="outline" onClick={exportCsv} data-testid={`${testid}-csv`}><Download className="mr-2 h-4 w-4" aria-hidden="true" /> Export CSV</Button>}
      </div>
      <div className="overflow-x-auto rounded-xl border border-slate-200 bg-white">
        <table className="w-full min-w-[640px] text-left text-sm">
          <thead className="bg-slate-50 text-xs uppercase tracking-wide text-slate-600">
            <tr>
              {selectable && <th className="w-10 px-3 py-3"><Checkbox checked={allSel} onCheckedChange={(v) => onSelect(v ? [...new Set([...selected, ...items.map((i) => i[rowKey])])] : selected.filter((s) => !items.some((i) => i[rowKey] === s)))} aria-label="Select all on page" data-testid={`${testid}-select-all`} /></th>}
              {columns.map((c) => (
                <th key={c.key} className="px-3 py-3 font-semibold">
                  {c.sortable ? (
                    <button type="button" onClick={() => toggleSort(c.key)} className="inline-flex items-center gap-1 rounded-sm hover:text-slate-900" data-testid={`${testid}-sort-${c.key}`}>
                      {c.label} {sort === c.key && (dir === "asc" ? <ArrowUp className="h-3 w-3" /> : <ArrowDown className="h-3 w-3" />)}
                    </button>
                  ) : c.label}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {items.map((row) => (
              <tr key={row[rowKey]} className="border-t border-slate-100 hover:bg-slate-50" data-testid={`${testid}-row`}>
                {selectable && <td className="px-3 py-2.5"><Checkbox checked={selected.includes(row[rowKey])} onCheckedChange={(v) => onSelect(v ? [...selected, row[rowKey]] : selected.filter((s) => s !== row[rowKey]))} aria-label="Select row" /></td>}
                {columns.map((c) => <td key={c.key} className="px-3 py-2.5 align-top text-slate-800">{c.render ? c.render(row) : row[c.key] ?? ""}</td>)}
              </tr>
            ))}
          </tbody>
        </table>
        {data && !items.length && <p className="p-8 text-center text-sm text-slate-600" data-testid={`${testid}-empty`}>No data yet</p>}
        {!data && <p className="p-8 text-center text-sm text-slate-600">Loading…</p>}
      </div>
      <div className="mt-3 flex items-center justify-between text-sm text-slate-700">
        <span data-testid={`${testid}-total`}>{data ? `${data.total} total` : ""}</span>
        <div className="flex items-center gap-2">
          <Button size="sm" variant="outline" disabled={page <= 1} onClick={() => setPage(page - 1)} aria-label="Previous page" data-testid={`${testid}-prev`}><ChevronLeft className="h-4 w-4" /></Button>
          <span>Page {page} of {pages}</span>
          <Button size="sm" variant="outline" disabled={page >= pages} onClick={() => setPage(page + 1)} aria-label="Next page" data-testid={`${testid}-next`}><ChevronRight className="h-4 w-4" /></Button>
        </div>
      </div>
    </div>
  );
}

export const fmtDate = (s) => (s ? new Date(s).toLocaleString() : "—");
